<script setup lang="ts">
import TextDivider from "@/components/TextDivider.vue";
import { useRedirectTargets } from "@/composables/useRedirectTargets";
import LoggedOut from "@/layouts/LoggedOut.vue";
import type { redirectSchema } from "@/router/dtos";
import { ref } from "vue";
import { useRouter } from "vue-router";
import * as z from "zod/v4-mini";
import { useLogin } from "./useLogin";
import { usePasskeyLogin } from "./usePasskeyLogin";

const { redirect } = defineProps<{
  redirect: z.infer<typeof redirectSchema>;
}>();

const router = useRouter();

const redirectTarget = useRedirectTargets(redirect);
const { login } = useLogin({
  onSuccess: () => router.push({ name: "verifyLogin", query: redirect }),
});
const {
  loginWithPasskey,
  isError: passkeyError,
  isPending: passkeyPending,
} = usePasskeyLogin({
  onSuccess: () => router.push(redirectTarget.value ?? { name: "home" }),
});
const email = ref("");
</script>

<template>
  <LoggedOut>
    <h1>Login</h1>
    <p><strong>Hey, schön dich zu sehen!</strong></p>
    <div class="column column-lg">
      <div class="column column-md">
        <form @submit.prevent="login(email)">
          <div class="column column-md">
            <label for="email">
              Bitte melde dich hier mit deiner E-Mail-Adresse oder einem Passkey
              an:
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              name="email"
              autocomplete="email"
              required
              autofocus
              placeholder="E-Mail-Adresse"
            />
            <button type="submit" class="button fullwidth">Login</button>
          </div>
        </form>
        <p class="center">oder</p>
        <div>
          <p v-if="passkeyError" class="error">
            Passkey-Anmeldung fehlgeschlagen. Bitte erneut versuchen.
          </p>
          <button
            type="button"
            class="button fullwidth"
            :disabled="passkeyPending"
            @click="loginWithPasskey()"
          >
            Mit Passkey anmelden
          </button>
        </div>
      </div>
      <div class="column">
        <TextDivider>Noch keinen Account?</TextDivider>
        <RouterLink
          :to="{ name: 'signup' }"
          class="button button--secondary fullwidth"
          role="button"
        >
          Account erstellen
        </RouterLink>
      </div>
    </div>
  </LoggedOut>
</template>

<style scoped>
.fullwidth {
  width: 100%;
  justify-content: center;
}

.error {
  color: var(--red-400);
}
</style>
